import { ArrowRight, Baseball, CheckCircle, Plus, Refresh, SeaWaves } from "@mynaui/icons-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { addTournament } from "../../api/tournaments";
import { AddTournamentDTO } from "../../types/tournament";
import { CircleDot, Sun, Target, Volleyball } from "lucide-react";
import moment from "moment";
import { getTournamentTypes } from "../../api/seasons";
import { getSeasonsByType } from "../../api/seasons";
import { ITournamentType } from "../../models/ISeason";
import { ISeason } from "../../models/ISeason";
import { ILocation } from "../../models/ILocation";
import { getAllLocations, createLocation } from "../../api/locations";
import { uploadTournamentCover, base64ToFile } from "../../services/firebase";
import { applyWatermark } from "../../utils/watermark";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFutbol } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Card,
  DatePicker,
  EmptyState,
  Field,
  FileButton,
  FormGrid,
  Input,
  Loader,
  PageHeader,
  Select,
  Tag,
  Textarea,
} from "../../ui";
import type { TagTone } from "../../ui";
import "./styles.scss";

const STEPS_LIST = [
  { title: "Tournament Name & Description", description: "Start by giving your tournament a name and a description" },
  { title: "Select Sport Type", description: "Choose the sport for your tournament" },
  { title: "When & Where?", description: "Select the dates and location of the tournament" },
  {
    title: "Final Touches",
    description:
      "Give your tournament a final flourish with a logo and a cover photo. You can choose to skip this step, if you’d like.",
  },
];

// `accent` colours the sport's icon: padel blue, volleyball orange, beach volleyball yellow, the rest neutral.
const TOURNAMENT_TYPES = [
  { name: "Volleyball", accent: "volleyball", icon: <Volleyball size={40} strokeWidth={1.5} /> },
  { name: "Beach Volleyball", accent: "beach-volleyball", icon: <SeaWaves size={40} /> },
  { name: "Padel", accent: "padel", icon: <Baseball size={40} strokeWidth={1.5} /> },
  { name: "Beach Tennis", accent: "neutral", icon: <Sun size={40} strokeWidth={1.5} /> },
  { name: "Spikeball", accent: "neutral", icon: <CircleDot size={40} strokeWidth={1.5} /> },
  { name: "Dodgeball", accent: "neutral", icon: <Target size={40} strokeWidth={1.5} /> },
  { name: "Beach Football", accent: "neutral", icon: <FontAwesomeIcon icon={faFutbol} style={{ fontSize: 40 }} /> },
];

const SPORT_TAG_TONES: Record<string, TagTone> = {
  Volleyball: "spark",
  "Beach Volleyball": "yellow",
  Padel: "brand",
};

const AddTournament = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const step = searchParams.get("step");

  const currentStep = parseInt(step ?? "1");
  const currentStepItem = currentStep > STEPS_LIST.length ? null : STEPS_LIST[step ? currentStep - 1 : 0];

  const [isCreated, setIsCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tournamentId, setTournamentId] = useState("");
  const [tournament, setTournament] = useState<AddTournamentDTO>({
    type: "Volleyball",
    cover: "",
    description: "",
    endDate: "",
    tournamentFeatures: [],
    locationId: "",
    name: "",
    startDate: "",
    specificDates: [],
    seasonId: "",
  });
  const [tournamentTypes, setTournamentTypes] = useState<ITournamentType[]>([]);
  const [seasons, setSeasons] = useState<ISeason[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
  const [showNewLocationInput, setShowNewLocationInput] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationLink, setNewLocationLink] = useState("");
  const [creatingLocation, setCreatingLocation] = useState(false);

  const handleNext = async () => {
    if (isNextDisabled()) return;

    if (currentStep === STEPS_LIST.length) {
      setLoading(true);
      try {
        const specificDatesForPayload = (tournament.specificDates || []).map(
          (d) => new Date(moment(d).format("YYYY-MM-DD"))
        );
        const response = await addTournament({
          ...tournament,
          startDate: tournament.startDate,
          endDate: tournament.endDate,
          specificDates: specificDatesForPayload,
          ...(tournament.seasonId && { seasonId: tournament.seasonId }),
        });
        setTournamentId(response.data.id);
        setIsCreated(true);
      } catch (error: any) {
        console.error("ADD TOURNAMENT", error.message);
      } finally {
        setLoading(false);
      }
    }

    const urlParams = new URLSearchParams();
    urlParams.append("step", `${currentStep + 1}`);

    const goTo = searchParams.get("goTo");
    if (goTo) urlParams.append("goTo", goTo);

    setSearchParams(urlParams);
  };

  const handleBack = () => {
    const urlParams = new URLSearchParams();
    urlParams.append("step", `${currentStep - 1}`);

    const goTo = searchParams.get("goTo");
    if (goTo) urlParams.append("goTo", goTo);

    setSearchParams(urlParams);
  };

  const inputChangeHandler = (field: string, value: string | Date[]) => {
    setTournament((prev) => ({ ...(prev as AddTournamentDTO), [field]: value }));
  };

  const isNextDisabled = () => {
    if (currentStep === 1) return !tournament.name || !tournament.description;
    if (currentStep === 3) return !tournament.startDate || !tournament.endDate || !tournament.locationId;
  };

  const handleFileChange = (files: FileList) => {
    const file = files?.[0];
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      onImageChange(file);
    }
  };

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        if (!e.target?.result) return reject("Failed to read image.");
        img.src = e.target.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_WIDTH = 800;
        const scaleFactor = MAX_WIDTH / img.width;

        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleFactor;

        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Failed to get canvas context.");

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7)); // compressed base64
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const onImageChange = async (file: File) => {
    try {
      // Resize the image first
      const base64 = await resizeImage(file);
      // Convert resized base64 back to file for upload
      const resizedFile = await base64ToFile(base64, "tournament-cover.jpg");
      // Apply watermark
      const watermarkedFile = await applyWatermark(resizedFile, tournament.name);
      // Upload to Firebase Storage (use temp ID since tournament doesn't exist yet)
      const downloadURL = await uploadTournamentCover(`temp_${Date.now()}`, watermarkedFile);
      inputChangeHandler("cover", downloadURL);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    document.title = "Create New Tournament";

    // Check for prefilled tournament data from sessionStorage
    const prefilledData = sessionStorage.getItem("prefilledTournament");
    if (prefilledData) {
      try {
        const parsed = JSON.parse(prefilledData);
        // Convert specificDates strings back to Date objects
        const specificDates =
          parsed.specificDates && Array.isArray(parsed.specificDates)
            ? parsed.specificDates.map((d: string | Date) => (d instanceof Date ? d : new Date(d)))
            : [];
        setTournament({
          name: parsed.name || "",
          description: parsed.description || "",
          type: parsed.type || "Volleyball",
          startDate: parsed.startDate || "",
          endDate: parsed.endDate || "",
          locationId: parsed.locationId || "",
          specificDates: specificDates,
          cover: parsed.cover || "",
          tournamentFeatures: parsed.tournamentFeatures || [],
          seasonId: parsed.seasonId || "",
        });
        // Clear the sessionStorage after loading
        sessionStorage.removeItem("prefilledTournament");
      } catch (error) {
        console.error("Error parsing prefilled tournament data:", error);
      }
    }

    const urlParams = new URLSearchParams();
    const goTo = searchParams.get("goTo");
    if (goTo) urlParams.append("goTo", goTo);
    urlParams.append("step", "1");

    if (currentStep > 1 && !tournament.name) {
      const urlParams = new URLSearchParams();
      urlParams.append("step", "1");
      urlParams.append("reset", "true");
    }

    setSearchParams(urlParams);
    loadTournamentTypes();
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoadingLocations(true);
    try {
      const response = await getAllLocations();
      setLocations(response.data || []);
    } catch (error: any) {
      console.error("Error loading locations:", error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleCreateNewLocation = async () => {
    if (!newLocationName.trim()) return;

    setCreatingLocation(true);
    try {
      const response = await createLocation({
        name: newLocationName.trim(),
        googleMapsLink: newLocationLink.trim() || undefined,
      });
      // Add the new location to the list and select it
      setLocations((prev) => [...prev, response.data]);
      inputChangeHandler("locationId", response.data.id);
      setSelectedLocation(response.data);
      // Reset the new location form
      setNewLocationName("");
      setNewLocationLink("");
      setShowNewLocationInput(false);
    } catch (error: any) {
      console.error("Error creating location:", error);
    } finally {
      setCreatingLocation(false);
    }
  };

  const loadTournamentTypes = async () => {
    try {
      const response = await getTournamentTypes();
      setTournamentTypes(response.data);
    } catch (error: any) {
      console.error("Error loading tournament types:", error);
    }
  };

  useEffect(() => {
    const loadSeasonsForType = async () => {
      if (!tournament.type || tournamentTypes.length === 0) {
        setSeasons([]);
        return;
      }

      const selectedType = tournamentTypes.find((t) => t.typeName === tournament.type);
      if (!selectedType) {
        setSeasons([]);
        return;
      }

      setLoadingSeasons(true);
      try {
        const response = await getSeasonsByType(selectedType.id);
        setSeasons(response.data);
        // Clear season selection if current season is not in the new list
        if (tournament.seasonId && !response.data.find((s) => s.id === tournament.seasonId)) {
          inputChangeHandler("seasonId", "");
        }
      } catch (error: any) {
        console.error("Error loading seasons:", error);
        setSeasons([]);
      } finally {
        setLoadingSeasons(false);
      }
    };

    loadSeasonsForType();
  }, [tournament.type, tournamentTypes]);

  return (
    <div className="add-tournament-page">
      {currentStepItem && <PageHeader title={currentStepItem?.title} subtitle={currentStepItem?.description} />}

      <div className="content">
        {loading ? (
          <Loader minHeight={300} />
        ) : isCreated ? (
          <Card className="tournament-created-container">
            <EmptyState
              icon={<CheckCircle />}
              title="Tournament Created!"
              description="Congrats! You're all set"
              action={
                <>
                  <Button onClick={() => navigate(`/dashboard/tournaments/${tournamentId}`, { replace: true })}>
                    Go to Tournament
                  </Button>
                  <Button variant="ghost" onClick={() => navigate("/")}>
                    Back Home
                  </Button>
                </>
              }
            />
          </Card>
        ) : currentStep === 1 ? (
          <Card className="tournament-name-section">
            <Input
              value={tournament?.name}
              onChange={(evt) => inputChangeHandler("name", evt.target.value)}
              placeholder="e.g. Winners Cup"
            />
            <Textarea
              value={tournament?.description}
              onChange={(evt) => inputChangeHandler("description", evt.target.value)}
              placeholder="Tournament description"
              rows={6}
            />
          </Card>
        ) : currentStep === 2 ? (
          <div className="tournament-type-section">
            {TOURNAMENT_TYPES.map((item, id) => {
              const isSelected = item.name === tournament.type;
              return (
                <button
                  type="button"
                  key={id}
                  className={`tournament-type tournament-type--${item.accent} ${isSelected ? "selected" : ""}`}
                  aria-pressed={isSelected}
                  onClick={() => {
                    inputChangeHandler("type", item.name);
                    inputChangeHandler("seasonId", "");
                  }}
                >
                  <span className="tournament-type__icon">{item.icon}</span>
                  <span className="tournament-type__name">{item.name}</span>
                </button>
              );
            })}
          </div>
        ) : currentStep === 3 ? (
          <Card className="tournament-dates-section">
            <Field label="Season">
              {(id) => (
                <Select
                  id={id}
                  block
                  value={tournament.seasonId || ""}
                  onChange={(e) => inputChangeHandler("seasonId", e.target.value)}
                  disabled={loadingSeasons || !tournament.type}
                >
                  <option value="">Select a season</option>
                  {seasons.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <FormGrid columns={2}>
              <Field label="Start date" required>
                {(id) => (
                  <DatePicker
                    id={id}
                    selected={tournament.startDate ? new Date(tournament.startDate) : null}
                    onChange={(date) => {
                      inputChangeHandler("endDate", "");
                      inputChangeHandler("specificDates", []);
                      inputChangeHandler("startDate", date ? moment(date).format("YYYY-MM-DD") : "");
                    }}
                    dateFormat="dd/MM/YYYY"
                    minDate={new Date()}
                    placeholderText="Pick a date"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    dropdownMode="select"
                  />
                )}
              </Field>
              <Field label="End date" required>
                {(id) => (
                  <DatePicker
                    id={id}
                    selected={tournament.endDate ? new Date(tournament.endDate) : null}
                    onChange={(date) => {
                      inputChangeHandler("specificDates", []);
                      inputChangeHandler("endDate", date ? moment(date).format("YYYY-MM-DD") : "");
                    }}
                    dateFormat="dd/MM/YYYY"
                    minDate={tournament.startDate ? new Date(tournament.startDate) : new Date()}
                    placeholderText="Pick a date"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    dropdownMode="select"
                  />
                )}
              </Field>
            </FormGrid>
            <Field
              label="Specific Dates"
              hint={
                (tournament.specificDates || []).length > 0
                  ? (tournament.specificDates || []).map((d) => moment(d).format("DD/MM/YYYY")).join(", ")
                  : undefined
              }
            >
              {(id) => (
                <DatePicker
                  id={id}
                  selected={null}
                  placeholderText="Pick dates"
                  highlightDates={tournament.specificDates || []}
                  minDate={tournament.startDate ? new Date(tournament.startDate) : undefined}
                  maxDate={tournament.endDate ? new Date(tournament.endDate) : undefined}
                  onChange={(date: Date | null) => {
                    if (!date) return;
                    const normalized = new Date(date.getFullYear(), date.getMonth(), date.getDate()); // local midnight
                    const currentDates = tournament.specificDates || [];
                    const exists = currentDates.some(
                      (d) => moment(d).format("YYYY-MM-DD") === moment(normalized).format("YYYY-MM-DD")
                    );
                    const updated = exists
                      ? currentDates.filter(
                          (d) => moment(d).format("YYYY-MM-DD") !== moment(normalized).format("YYYY-MM-DD")
                        )
                      : [...currentDates, normalized];

                    const sorted = updated.sort((a, b) => a.getTime() - b.getTime());
                    inputChangeHandler("specificDates", sorted);
                  }}
                  dayClassName={(date) =>
                    (tournament.specificDates || []).some(
                      (d) => moment(d).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD")
                    )
                      ? "selected-day"
                      : ""
                  }
                />
              )}
            </Field>

            <Field label="Location" required>
              {(id) =>
                showNewLocationInput ? (
                  <div className="new-location-form">
                    <Input
                      id={id}
                      placeholder="Location name"
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      disabled={creatingLocation}
                    />
                    <Input
                      placeholder="Google Maps link (optional)"
                      value={newLocationLink}
                      onChange={(e) => setNewLocationLink(e.target.value)}
                      disabled={creatingLocation}
                    />
                    <div className="new-location-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowNewLocationInput(false);
                          setNewLocationName("");
                          setNewLocationLink("");
                        }}
                        disabled={creatingLocation}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleCreateNewLocation}
                        disabled={creatingLocation || !newLocationName.trim()}
                        loading={creatingLocation}
                      >
                        Create
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="location-picker">
                    <Select
                      id={id}
                      block
                      value={tournament.locationId || ""}
                      onChange={(e) => {
                        const newLocationId = e.target.value || "";
                        inputChangeHandler("locationId", newLocationId);
                        const loc = locations.find((l) => l.id === newLocationId);
                        setSelectedLocation(loc || null);
                      }}
                      disabled={loadingLocations}
                    >
                      <option value="">Select a location</option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </Select>
                    <Button variant="link" size="sm" icon={<Plus size={16} />} onClick={() => setShowNewLocationInput(true)}>
                      Add new location
                    </Button>
                  </div>
                )
              }
            </Field>
          </Card>
        ) : currentStep === 4 ? (
          <Card className="final-touches-section" padding="none">
            <div className="cover-photo-container">
              {tournament.cover && <img src={tournament.cover} className="cover" alt="" />}
              <FileButton
                variant="tint"
                size="sm"
                className="add-cover-btn"
                accept="image/*"
                icon={tournament.cover ? <Refresh size={16} /> : <Plus size={16} />}
                onFiles={handleFileChange}
              >
                {tournament.cover ? "Change" : "Add"} cover photo
              </FileButton>
            </div>
            <div className="tournament-details">
              <p className="name">{tournament.name}</p>
              <div className="details-container">
                <p className="detail">{selectedLocation?.name || locations.find(l => l.id === tournament.locationId)?.name || "-"}</p>
                <div className="separator" />
                <Tag tone={SPORT_TAG_TONES[tournament.type] ?? "neutral"}>{tournament.type}</Tag>
                <div className="separator" />
                <p className="detail">Starts {moment(tournament.startDate).format("DD MMM YYYY")}</p>
                <div className="separator" />
                <p className="detail">Ends {moment(tournament.endDate).format("DD MMM YYYY")}</p>
              </div>
              <p className="description">{tournament.description}</p>
              {/* <button className="add-feature-btn">
                <Plus size={20} color="#004AAD" /> Add Feature
              </button> */}
            </div>
          </Card>
        ) : null}
      </div>

      {currentStepItem && (
        <div className="footer">
          {currentStep !== 1 && (
            <Button variant="ghost" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button
            disabled={!!isNextDisabled()}
            onClick={handleNext}
            iconRight={STEPS_LIST.length !== currentStep ? <ArrowRight size={20} /> : undefined}
          >
            {STEPS_LIST.length === currentStep ? "Finish" : "Next"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddTournament;
